/**
 * TradesPane Component
 *
 * Live trading cockpit — Trades feature pane body.
 *
 * Layout (top → bottom):
 *   1. Action area — large "+ New Trade" and "+ New Opportunity" buttons
 *   2. Quick Trades section — preset buttons (when showQuickTrades && presets.length > 0)
 *                            or "Configure" empty state
 *   3. Filter tabs — All | Open | Shelved | Opportunities (with counts)
 *   4. Trade list — scrollable; empty state when no trades
 *
 * Pane-width responsive via ResizeObserver:
 *   - collapsed (< 260px): action area + quick trades only, trade list hidden
 *   - normal    (≥ 260px): full layout
 *   - wide      (≥ 500px): TODO T06 — 2-column card grid
 *
 * T05: Loads LiveTradingSettings on mount. Gear icon opens QuickTradeSettingsModal.
 *      Preset buttons open TradeDrawer with preset pre-applied.
 *      "Configure a Quick Trade" empty state opens modal with form pre-opened.
 *
 * Memoized via React.memo — re-renders only when trades or callbacks change.
 *
 * @see FeaturePaneShell — pane container; TradesPane renders as its children
 * @see TradeDrawer — trade creation/edit form drawer
 * @see QuickTradeSettingsModal — gear modal for display settings + preset management
 * @see liveTradingSettingsService — settings persistence
 * @see SessionDraftService.updateTrades — persistence (T04+)
 * @see TradeDraft — trade data model
 */

// 1. External dependencies
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ChartCandlestick, Settings2 } from 'lucide-react';

// 2. Internal absolute imports
import { Button, Divider, EmptyState } from '@enfomentis/ui';
import { liveTradingSettingsService } from '@/services/LiveTradingSettingsService';

// 3. Type imports
import type { Confluence } from '@/types/schema/Confluence';
import type { EntryModel } from '@/types/schema/EntryModel';
import type { Playbook } from '@/types/schema/Playbook';
import type { PlaybookCriteriaItem } from '@/types/schema/PlaybookCriteriaItem';
import type { Strategy } from '@/types/schema/Strategy';
import type { TradeDraft } from '@/types/transient/TradeDraft';
import type { QuickTradePreset, LiveTradingSettings } from '@/types/schema/LiveTradingSettings';

// 4. Relative imports
import { TradeCard } from './TradeCard';
import { TradeDrawer } from './TradeDrawer/TradeDrawer';
import { QuickTradeSettingsModal } from './QuickTradeSettingsModal';

// ===== TYPES =====

type TradeFilter = 'all' | 'open' | 'shelved' | 'opportunities';

/**
 * T05 (WU2E): Callbacks and data exposed upward to `LiveTradingPhase` so the
 * FeaturesDropdown Trades accordion can trigger drawer/modal actions from the
 * TradingHub header.
 */
export interface TradesPaneActions {
  newTrade: () => void;
  presetOpen: (preset: QuickTradePreset) => void;
  openSettings: () => void;
  presets: QuickTradePreset[];
  showQuickTrades: boolean;
}

interface TradesPaneProps {
  /** Current session's trade drafts — filtered and displayed in the list */
  trades: TradeDraft[];
  /** Called with the full updated trades array after each commit */
  onUpdateTrades?: (trades: TradeDraft[]) => Promise<void>;
  /** Open trade creation modal (optional external callback) */
  onNewTrade?: () => void;
  /**
   * Session draft ID — used to stamp new drafts with canonical ownership.
   * Optional: drafts are still created without it but lack carry-forward lineage.
   */
  sessionDraftId?: string;
  /**
   * Default account ID for new trade drafts — sourced from the session draft's
   * `defaultAccountId` and forwarded to TradeDrawer for auto-population.
   */
  defaultAccountId?: string;
  /**
   * T05 (WU2E): Called whenever actions or quick-trade data change so that
   * `LiveTradingPhase` can build the FeaturesDropdown trades menu content.
   * Fires once after settings load, then again if settings are saved.
   */
  onActionsChange?: (actions: TradesPaneActions) => void;
  /** Active strategies loaded by LiveTradingPhase — passed to TradeDrawer + QuickTradeSettingsModal */
  activeStrategies?: Strategy[];
  /** Active playbooks loaded by LiveTradingPhase */
  activePlaybooks?: Playbook[];
  /** Active criteria loaded by LiveTradingPhase */
  activeCriteria?: PlaybookCriteriaItem[];
  /** Active confluences loaded by LiveTradingPhase */
  activeConfluences?: Confluence[];
  /** Active entry models loaded by LiveTradingPhase */
  activeEntryModels?: EntryModel[];
}

// ===== CONSTANTS =====

const FILTER_TABS: { key: TradeFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'shelved', label: 'Shelved' },
  { key: 'opportunities', label: 'Opportunities' },
];

/** Pane width below which trade list is hidden (action area only) */
const COLLAPSED_WIDTH_PX = 260;

// ===== COMPONENT =====

export const TradesPane: React.FC<TradesPaneProps> = React.memo(({
  trades,
  onUpdateTrades,
  onNewTrade,
  sessionDraftId,
  defaultAccountId,
  onActionsChange,
  activeStrategies = [],
  activePlaybooks = [],
  activeCriteria = [],
  activeConfluences = [],
  activeEntryModels = [],
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  /** Bug 4: synced in render body so commit/cancel handlers always read latest trades */
  const tradesRef = useRef(trades);
  tradesRef.current = trades;
  /** Bug 3: serializes concurrent writes — prevents auto-save from racing a commit/cancel */
  const pendingWriteRef = useRef<Promise<unknown> | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeFilter, setActiveFilter] = useState<TradeFilter>('all');
  /** Fix 5: surfaces write errors to the user */
  const [commitError, setCommitError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Settings state (loaded from LiveTradingSettingsService on mount)
  // ---------------------------------------------------------------------------

  const [showQuickTrades, setShowQuickTrades] = useState(false);
  const [quickTradePresets, setQuickTradePresets] = useState<QuickTradePreset[]>([]);
  const [activeTradesDefaultSort, setActiveTradesDefaultSort] = useState<LiveTradingSettings['activeTradesDefaultSort']>('newest');
  const [activeTradesCardInfo, setActiveTradesCardInfo] = useState<LiveTradingSettings['activeTradesCardInfo']>([]);

  useEffect(() => {
    let mounted = true;
    liveTradingSettingsService.getOrCreateDefaultSettings().then((settings) => {
      if (!mounted) return;
      setShowQuickTrades(settings.showQuickTrades ?? false);
      setQuickTradePresets(settings.quickTradePresets ?? []);
      setActiveTradesDefaultSort(settings.activeTradesDefaultSort ?? 'newest');
      setActiveTradesCardInfo(settings.activeTradesCardInfo ?? []);
    }).catch(() => { /* silent */ });
    return () => { mounted = false; };
  }, []);

  // ---------------------------------------------------------------------------
  // Settings modal state
  // ---------------------------------------------------------------------------

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  /** When true, the modal auto-opens the Add Preset form (for empty-state CTA) */
  const [settingsOpenPresetForm, setSettingsOpenPresetForm] = useState(false);

  const handleOpenSettings = useCallback((withForm = false): void => {
    setSettingsOpenPresetForm(withForm);
    setIsSettingsOpen(true);
  }, []);

  const handleCloseSettings = useCallback((): void => {
    setIsSettingsOpen(false);
  }, []);

  const handleSaveSettings = useCallback(async (updates: {
    showQuickTrades: boolean;
    quickTradePresets: QuickTradePreset[];
    activeTradesDefaultSort: LiveTradingSettings['activeTradesDefaultSort'];
    activeTradesCardInfo: LiveTradingSettings['activeTradesCardInfo'];
  }): Promise<void> => {
    await liveTradingSettingsService.updateQuickTradesSettings(updates);
    setShowQuickTrades(updates.showQuickTrades);
    setQuickTradePresets(updates.quickTradePresets);
    setActiveTradesDefaultSort(updates.activeTradesDefaultSort);
    setActiveTradesCardInfo(updates.activeTradesCardInfo);
  }, []);

  // ---------------------------------------------------------------------------
  // Trade Drawer state
  // ---------------------------------------------------------------------------

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'new' | 'edit'>('new');
  /** ID of the TradeDraft being edited; undefined in new mode */
  const [editDraftId, setEditDraftId] = useState<string | undefined>(undefined);
  /** Quick Trade preset to pre-apply on next drawer open (new mode only) */
  const [activeQuickTradePreset, setActiveQuickTradePreset] = useState<QuickTradePreset | undefined>(undefined);

  const handleNewTrade = useCallback((): void => {
    setDrawerMode('new');
    setEditDraftId(undefined);
    setActiveQuickTradePreset(undefined);
    setIsDrawerOpen(true);
    onNewTrade?.();
  }, [onNewTrade]);

  const handlePresetButtonPress = useCallback((preset: QuickTradePreset): void => {
    setDrawerMode('new');
    setEditDraftId(undefined);
    setActiveQuickTradePreset(preset);
    setIsDrawerOpen(true);
  }, []);

  // ---------------------------------------------------------------------------
  // T05 (WU2E): Expose actions upward for FeaturesDropdown trades menu
  // ---------------------------------------------------------------------------

  /** Holds latest onActionsChange without including it in effect deps */
  const onActionsChangeRef = useRef(onActionsChange);
  onActionsChangeRef.current = onActionsChange;

  useEffect(() => {
    onActionsChangeRef.current?.({
      newTrade: handleNewTrade,
      presetOpen: handlePresetButtonPress,
      openSettings: handleOpenSettings,
      presets: quickTradePresets,
      showQuickTrades,
    });
  }, [handleNewTrade, handlePresetButtonPress, handleOpenSettings, quickTradePresets, showQuickTrades]);

  /**
   * Open the appropriate drawer for an existing TradeDraft.
   * - artifactKind 'trade' → opens TradeDrawer in edit mode
   * - artifactKind 'opportunity' → OpportunityDrawer (WU03 scope; no-op in WU2A)
   */
  const handleEditDraft = useCallback((draftId: string): void => {
    const draft = trades.find((t) => t.id === draftId);
    if (!draft) return;
    if (draft.artifactKind === 'opportunity') {
      // OpportunityDrawer is WU03 scope — no-op for now
      return;
    }
    setDrawerMode('edit');
    setEditDraftId(draftId);
    setActiveQuickTradePreset(undefined);
    setIsDrawerOpen(true);
  }, [trades]);

  const handleCloseDrawer = useCallback((): void => {
    setIsDrawerOpen(false);
    setActiveQuickTradePreset(undefined);
  }, []);

  /** Existing draft for the currently open edit session — undefined in new mode */
  const existingDraftForEdit = editDraftId !== undefined
    ? trades.find((t) => t.id === editDraftId)
    : undefined;

  // Narrow to TradeSetupDraft for TradeDrawer (it only handles trade drafts)
  const tradeSetupDraftForEdit = existingDraftForEdit?.artifactKind === 'trade'
    ? existingDraftForEdit
    : undefined;

  /**
   * Called by TradeDrawer on any successful CTA, and also by auto-save.
   * Appends new drafts or replaces existing ones by id, then propagates up.
   * Fix 5: on write rejection, surfaces the error and re-throws so TradeDrawer
   * also catches it (keeps the drawer open). Rollback is implicit — the parent's
   * trades state is only updated after a successful write.
   */
  const handleCommit = useCallback(async (draft: TradeDraft): Promise<void> => {
    // Bug 3+4: read tradesRef for latest value; chain off pendingWriteRef to serialize writes
    const doWrite = async (): Promise<void> => {
      const current = tradesRef.current;
      const updatedTrades = current.some((t) => t.id === draft.id)
        ? current.map((t) => (t.id === draft.id ? draft : t))
        : [...current, draft];
      await onUpdateTrades?.(updatedTrades);
    };
    const pending = pendingWriteRef.current;
    pendingWriteRef.current = pending !== null ? pending.catch(() => {}).then(doWrite) : doWrite();
    try {
      await pendingWriteRef.current;
      setCommitError(null);
    } catch (err) {
      setCommitError('Failed to save trade. Please try again.');
      throw err;
    }
  }, [onUpdateTrades]);

  /**
   * Called by TradeDrawer when new-mode drawer closes without commit.
   * Removes the auto-saved draft (if any) from the trades array.
   */
  const handleCancelDraft = useCallback(async (draftId: string): Promise<void> => {
    // Bug 3+4: read tradesRef for latest value; chain off pendingWriteRef to serialize writes
    const doWrite = async (): Promise<void> => {
      const current = tradesRef.current;
      const updatedTrades = current.filter((t) => t.id !== draftId);
      if (updatedTrades.length < current.length) {
        await onUpdateTrades?.(updatedTrades);
      }
    };
    const pending = pendingWriteRef.current;
    pendingWriteRef.current = pending !== null ? pending.catch(() => {}).then(doWrite) : doWrite();
    await pendingWriteRef.current;
  }, [onUpdateTrades]);

  // ---------------------------------------------------------------------------
  // Pane-width responsive detection
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setIsCollapsed(entry.contentRect.width < COLLAPSED_WIDTH_PX);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ---------------------------------------------------------------------------
  // Filter counts and filtered list
  // ---------------------------------------------------------------------------

  const counts = useMemo(() => ({
    all: trades.length,
    open: trades.filter((t) => t.artifactKind === 'trade' && !t.isShelved && (t.workflowStatus === 'pending' || t.workflowStatus === 'active')).length,
    shelved: trades.filter((t) => t.isShelved).length,
    opportunities: trades.filter((t) => t.artifactKind === 'opportunity').length,
  }), [trades]);

  const filteredTrades = useMemo(() => {
    switch (activeFilter) {
      case 'open': return trades.filter((t) => t.artifactKind === 'trade' && !t.isShelved && (t.workflowStatus === 'pending' || t.workflowStatus === 'active'));
      case 'shelved': return trades.filter((t) => t.isShelved);
      case 'opportunities': return trades.filter((t) => t.artifactKind === 'opportunity');
      default: return trades;
    }
  }, [trades, activeFilter]);

  const sortedFilteredTrades = useMemo(() => {
    return [...filteredTrades].sort((a, b) => {
      switch (activeTradesDefaultSort) {
        case 'newest':       return b.createdAt.localeCompare(a.createdAt);
        case 'oldest':       return a.createdAt.localeCompare(b.createdAt);
        case 'alphabetical': return (a.tradeName ?? '').localeCompare(b.tradeName ?? '');
        default:             return 0;
      }
    });
  }, [filteredTrades, activeTradesDefaultSort]);

  // ---------------------------------------------------------------------------
  // Sorted presets for display (by order field)
  // ---------------------------------------------------------------------------

  const sortedPresets = useMemo(
    () => [...quickTradePresets].sort((a, b) => a.order - b.order),
    [quickTradePresets],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div ref={containerRef} className="h-full flex flex-col overflow-hidden">

      {/* ── Action Area ── */}
      <div className={`shrink-0 p-2 flex gap-2 ${isCollapsed ? 'flex-col' : 'flex-row'}`}>
        <Button
          color="primary"
          variant="solid"
          size="sm"
          className="flex-1"
          onPress={handleNewTrade}
        >
          + New Trade
        </Button>
        {/* OpportunityDrawer is WU03 scope — button is present but no-op in WU2A */}
        <Button
          color="default"
          variant="bordered"
          size="sm"
          className="flex-1"
        >
          + New Opportunity
        </Button>
      </div>

      {/* ── Quick Trades Section ── */}
      <div className="shrink-0 px-2 pb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-text-tertiary font-medium uppercase tracking-wide">
            Quick Trades
          </span>
          <Button
            size="sm"
            variant="light"
            isIconOnly
            aria-label="Quick Trade settings"
            onPress={() => handleOpenSettings(false)}
          >
            <Settings2 size={12} className="text-text-tertiary" />
          </Button>
        </div>

        {showQuickTrades && sortedPresets.length > 0 ? (
          /* Preset buttons */
          <div className="flex flex-wrap gap-1">
            {sortedPresets.map((preset) => (
              <Button
                key={preset.id}
                size="sm"
                variant="flat"
                color="primary"
                className="text-xs"
                onPress={() => handlePresetButtonPress(preset)}
              >
                {preset.name}
              </Button>
            ))}
          </div>
        ) : (
          /* Empty state / configure CTA */
          <Button
            size="sm"
            variant="bordered"
            color="default"
            className="w-full text-xs text-text-secondary border-dashed"
            onPress={() => handleOpenSettings(true)}
          >
            + Configure a Quick Trade
          </Button>
        )}
      </div>

      <Divider />

      {/* ── Settings Modal ── */}
      <QuickTradeSettingsModal
        isOpen={isSettingsOpen}
        onClose={handleCloseSettings}
        initialShowQuickTrades={showQuickTrades}
        initialQuickTradePresets={quickTradePresets}
        initialActiveTradesDefaultSort={activeTradesDefaultSort}
        initialActiveTradesCardInfo={activeTradesCardInfo}
        onSave={handleSaveSettings}
        openPresetForm={settingsOpenPresetForm}
        activeStrategies={activeStrategies}
        activePlaybooks={activePlaybooks}
        activeCriteria={activeCriteria}
        activeConfluences={activeConfluences}
        activeEntryModels={activeEntryModels}
      />

      {/* ── Trade Drawer ── */}
      <TradeDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        mode={drawerMode}
        {...(tradeSetupDraftForEdit !== undefined ? { existingDraft: tradeSetupDraftForEdit } : {})}
        {...(activeQuickTradePreset !== undefined ? { quickTradePreset: activeQuickTradePreset } : {})}
        {...(sessionDraftId !== undefined ? { sessionDraftId } : {})}
        {...(defaultAccountId !== undefined ? { defaultAccountId } : {})}
        onCommit={handleCommit}
        onAutoSave={handleCommit}
        onCancelDraft={handleCancelDraft}
        activeStrategies={activeStrategies}
        activePlaybooks={activePlaybooks}
        activeCriteria={activeCriteria}
        activeConfluences={activeConfluences}
        activeEntryModels={activeEntryModels}
      />

      {/* ── Commit error banner ── */}
      {commitError !== null && (
        <div className="shrink-0 px-2 py-1">
          <p className="text-xs text-danger">{commitError}</p>
        </div>
      )}

      {/* ── Filter Tabs + Trade List (hidden when collapsed) ── */}
      {!isCollapsed && (
        <>
          {/* Filter tabs */}
          <div className="shrink-0 flex items-center gap-0.5 px-1 py-1 overflow-x-auto">
            {FILTER_TABS.map(({ key, label }) => {
              const count = counts[key];
              const displayText = count > 0 ? `${label} (${count})` : label;
              const isActive = activeFilter === key;

              return (
                <Button
                  key={key}
                  size="sm"
                  variant={isActive ? 'flat' : 'light'}
                  color={isActive ? 'primary' : 'default'}
                  className="shrink-0 h-6 min-w-0 px-1.5 text-xs"
                  onPress={() => setActiveFilter(key)}
                >
                  {displayText}
                </Button>
              );
            })}
          </div>

          {/* Trade list */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {sortedFilteredTrades.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <EmptyState
                  icon={<ChartCandlestick size={24} className="text-primary" />}
                  title="No trades yet"
                  description={
                    activeFilter === 'all'
                      ? 'Click + New Trade above to log your first setup.'
                      : 'No trades match this filter.'
                  }
                  className="py-4!"
                />
              </div>
            ) : (
              <div className="p-2 flex flex-col gap-1.5">
                {sortedFilteredTrades.map((trade) => (
                  <TradeCard
                    key={trade.id}
                    trade={trade}
                    cardInfo={activeTradesCardInfo}
                    onEdit={handleEditDraft}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
});

TradesPane.displayName = 'TradesPane';
